"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Camera, Activity, Wrench, ShieldCheck, CircleCheck as CheckCircle2, ArrowRight, PhoneCall, Layers, CircleAlert as AlertTriangle } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
export default function PipeDiagnostics() {
  const shouldReduceMotion = useReducedMotion();
  const [activeTab, setActiveTab] = useState("inspection");
  const diagnosticMethods = [{
    id: "camera",
    title: "Fiber-Optic Sewer Camera Inspection",
    tag: "Non-Invasive",
    icon: Camera,
    description: "High-resolution self-leveling cameras navigate interior pipe walls to pinpoint root intrusion, cracked joints, bellied lines, and scale buildup with precise linear footage tracking.",
    points: ["Real-time 1080p color video recording", "Sonde transmitter for exact depth and surface location", "Applicable for 2-inch to 12-inch sewer and drain pipes"]
  }, {
    id: "acoustic",
    title: "Electro-Acoustic Leak Detection",
    tag: "Precision Locating",
    icon: Activity,
    description: "Specialized ground microphones and acoustic listening discs capture subsurface water frequency anomalies to detect pressurized slab and underground leaks without digging.",
    points: ["Detects concealed leaks beneath concrete slabs and soil", "Calibrated frequency filters eliminate ambient site noise", "Prevents unnecessary exploratory property demolition"]
  }, {
    id: "pressure",
    title: "Hydrostatic Pressure & Flow Testing",
    tag: "Integrity Verification",
    icon: Layers,
    description: "Isolates municipal feed and interior supply lines using calibrated test gauges to measure static drop, uncovering microscopic fissures before pipe failure occurs.",
    points: ["Pinpoints supply drop down to 0.5 PSI variance", "Verifies municipal pressure reducing valve (PRV) status", "Certified test reports for insurance claims"]
  }];
  const repairMethods = [{
    id: "cured-in-place",
    title: "Cured-In-Place Pipe (CIPP) Lining",
    category: "Trenchless Solution",
    lifespan: "50+ Year Design Life",
    description: "An epoxy-saturated felt liner is pulled or inverted into the damaged pipe and cured in place, creating a seamless, joint-free structural pipe inside the old host pipe.",
    specs: ["Zero landscape or driveway trenching required", "Stops root intrusion permanently with seamless lining", "Restores structural integrity in 4 to 8 hours"]
  }, {
    id: "pipe-bursting",
    title: "Hydraulic Pipe Bursting",
    category: "Full Line Replacement",
    lifespan: "100 Year Material Rating",
    description: "A hardened steel expander head breaks apart collapsed or deteriorated clay and cast iron pipe while simultaneously pulling high-density polyethylene (HDPE) in its place.",
    specs: ["Upgrades pipe diameter for higher drainage volume", "Seamless HDPE material resists all soil shift and corrosion", "Requires only two small access entry and exit pits"]
  }, {
    id: "spot-repair",
    title: "Targeted Sectional Mechanical Clamping",
    category: "Point Repair",
    lifespan: "Permanent Sectional Fix",
    description: "For localized breaks, heavy-gauge stainless steel sleeve clamps with EPDM gaskets seal ruptured sections immediately to restore urgent water flow.",
    specs: ["Immediate pressure tolerance up to 150 PSI", "Fast turn-around for emergency main line repairs", "Code-approved for potable and wastewater systems"]
  }];
  const pipelineStages = [{
    step: "01",
    name: "Visual & Sensor Scan",
    desc: "We feed fiber-optic optics and acoustic probes to find the exact millimeter coordinates of the fault."
  }, {
    step: "02",
    name: "Clearance & Hydro-Jetting",
    desc: "4,000 PSI hydro-scrubbing strips grease, scale, and roots to prepare raw pipe surfaces for adhesion."
  }, {
    step: "03",
    name: "Trenchless or Mechanical Fix",
    desc: "We deploy epoxy liner inversion, bursting heads, or precision clamp sleeves depending on pipe integrity."
  }, {
    step: "04",
    name: "Post-Repair Camera Verification",
    desc: "A final video sweep confirms full flow velocity, structural wall thickness, and warranty-backed sealing."
  }];
  return <section id="pipediagnosticsandrepair" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="outline" className="mb-3 px-3 py-1 font-mono uppercase tracking-wider">
            Technical Engineering & Diagnostics
          </Badge>
          <h2 className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            Pipe Diagnostics & Trenchless Repair
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            We use fiber-optic sewer cameras, acoustic ground sensors, and cured-in-place pipe (CIPP) lining to resolve hidden line fractures in Jonesboro without excavating yards.
          </p>
        </div>

        {/* Tabbed Technical Area */}
        <div className="mt-12 lg:mt-16">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center">
              <TabsList className="grid w-full max-w-md grid-cols-2 p-1">
                <TabsTrigger value="inspection" className="text-sm font-semibold">
                  Diagnostic Technologies
                </TabsTrigger>
                <TabsTrigger value="repair" className="text-sm font-semibold">
                  Repair Methodologies
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Diagnostics Tab Content */}
            <TabsContent value="inspection" className="mt-8 focus-visible:outline-none">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {diagnosticMethods.map((method, idx) => {
                const IconComp = method.icon;
                return <motion.div key={method.id} data-index={idx} initial={shouldReduceMotion ? {} : {
                  opacity: 0,
                  y: 16
                }} whileInView={shouldReduceMotion ? {} : {
                  opacity: 1,
                  y: 0
                }} viewport={{
                  once: true
                }} transition={{
                  duration: 0.4,
                  delay: idx * 0.1
                }}>
                      <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                        <CardHeader>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-primary">
                              <IconComp className="size-5" />
                            </div>
                            <Badge variant="secondary" className="font-mono text-xs">
                              {method.tag}
                            </Badge>
                          </div>
                          <CardTitle className="mt-3 font-default text-xl text-foreground">
                            {method.title}
                          </CardTitle>
                          <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                            {method.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <Separator className="mb-4" />
                          <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Key Diagnostic Specs
                          </p>
                          <ul className="space-y-2.5">
                            {method.points.map((pt, pIdx) => <li key={pIdx} className="flex items-start gap-2 text-sm text-foreground">
                                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                                <span>{pt}</span>
                              </li>)}
                          </ul>
                        </CardContent>
                      </Card>
                    </motion.div>;
              })}
              </div>
            </TabsContent>

            {/* Repair Tab Content */}
            <TabsContent value="repair" className="mt-8 focus-visible:outline-none">
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                {repairMethods.map((repair, idx) => <motion.div key={repair.id} data-index={idx} initial={shouldReduceMotion ? {} : {
                opacity: 0,
                y: 16
              }} whileInView={shouldReduceMotion ? {} : {
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: idx * 0.1
              }}>
                    <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                      <CardHeader>
                        <div className="flex items-center justify-between gap-2">
                          <Badge variant="default" className="text-xs">
                            {repair.category}
                          </Badge>
                          <span className="font-mono text-xs font-semibold text-muted-foreground">
                            {repair.lifespan}
                          </span>
                        </div>
                        <CardTitle className="mt-3 font-default text-xl text-foreground">
                          {repair.title}
                        </CardTitle>
                        <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                          {repair.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <Separator className="mb-4" />
                        <p className="mb-2 font-mono text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                          Engineering Specifications
                        </p>
                        <ul className="space-y-2.5">
                          {repair.specs.map((spec, sIdx) => <li key={sIdx} className="flex items-start gap-2 text-sm text-foreground">
                              <ShieldCheck className="mt-0.5 size-4 shrink-0 text-accent-foreground" />
                              <span>{spec}</span>
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  </motion.div>)}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Process Flow Breakdown */}
        <div className="mt-16 rounded-2xl border border-border bg-card p-6 sm:p-8 lg:p-10 shadow-sm">
          <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <h3 className="font-default text-2xl font-bold text-card-foreground">
                4-Stage Diagnostic to Restoration Protocol
              </h3>
              <p className="mt-1 text-sm text-muted-foreground">
                How our technicians isolate underground breaks and restore full line flow in Jonesboro.
              </p>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-muted px-3 py-1.5 font-mono text-xs text-muted-foreground">
              <AlertTriangle className="size-4 text-destructive" />
              <span>Zero Unplanned Excavation</span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pipelineStages.map((stg, i) => <div key={stg.step} data-index={i} className="relative flex flex-col rounded-xl border border-border bg-background p-5">
                <span className="font-mono text-3xl font-bold text-primary/30">
                  {stg.step}
                </span>
                <h4 className="mt-2 font-default text-base font-semibold text-foreground">
                  {stg.name}
                </h4>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  {stg.desc}
                </p>
              </div>)}
          </div>

          <Separator className="my-8" />

          {/* Urgent Emergency Callout */}
          <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-xl">
              <h4 className="font-default text-lg font-bold text-card-foreground">
                Need an immediate sewer camera evaluation or pipe repair?
              </h4>
              <p className="mt-1 text-sm text-muted-foreground">
                Jonesboro Emergency Plumber is on standby 24/7 with fully equipped diagnostic vehicles.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="font-medium">
                <a href="tel:4708479076" className="inline-flex items-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call 24/7 Dispatch</span>
                </a>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/problems/" className="inline-flex items-center gap-2">
                  <span>Common Pipe Issues</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
            </div>
          </div>
        </div>

      </div>
    </section>;
}